import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  msalConfig,
  b2cMsalConfig,
  apiScopes,
  b2cConfig,
} from "../conf/authConfig";
import ErrorDialog from "../components/ErrorDialog";
import { processFailedUploads } from "../utilities/postData";
import sharedConfig from "../../shared-config.json";
// Create context
const DualMsalContext = createContext(null);

// Custom hook for accessing the dual MSAL context
export const useDualMsal = () => {
  const context = useContext(DualMsalContext);
  if (!context) {
    throw new Error("useDualMsal must be used within a DualMsalProvider");
  }
  return context;
};

// Helper function to determine if an account is B2C based on iss field in token claims
const isB2CAccount = (account) => {
  if (!account || !account.idTokenClaims || !account.idTokenClaims.iss) {
    return false;
  }
  // B2C accounts have an issuer that contains 'b2clogin.com'
  return account.idTokenClaims.iss.includes("b2clogin.com");
};

// Inner provider that uses both MSAL instances
export function MsalProvider({ children }) {
  // MSAL instances - create them in the component to ensure proper lifecycle management
  const [msalInstance] = useState(
    () => new PublicClientApplication(msalConfig)
  );
  const [b2cMsalInstance] = useState(
    () => new PublicClientApplication(b2cMsalConfig)
  );

  const [initialized, setInitialized] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    title: "",
    message: "",
  });
  const isInitializing = useRef(false);

  const handleErrorDialogClose = () => {
    setErrorDialog({ ...errorDialog, open: false });
  };

  // Compute currentUser directly from currentAccount
  const getCurrentUser = useCallback((account) => {
    if (!account) return null;

    const isB2C = isB2CAccount(account);
    const accountType = isB2C ? "B2C" : "Entra ID";
    return {
      displayName: account.displayName || account.name,
      accountType: accountType,
      authSource: accountType === "B2C" ? "b2c" : "entra",
      roles:
        accountType === "B2C"
          ? [sharedConfig.roles.participant.name]
          : account.idTokenClaims?.roles || [],
      oid: account?.idTokenClaims?.oid,
    };
  }, []);

  // Logout the current user
  const logout = useCallback(() => {
    console.log(
      `Logging out ${isB2CAccount(currentAccount) ? "B2C" : "Entra ID"} user`
    );

    // Use the appropriate MSAL instance for logout
    const msalInstanceToUse = isB2CAccount(currentAccount)
      ? b2cMsalInstance
      : msalInstance;

    setCurrentAccount(null);

    // Redirect to home page after logout
    msalInstanceToUse
      .logoutRedirect({
        onRedirectNavigate: () => {
          window.location.href = "/";
          return false; // Prevent MSAL from navigating
        },
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  }, [currentAccount, b2cMsalInstance, msalInstance]);

  // Get access token for API calls
  const getToken = useCallback(
    async (forceRefresh = false) => {
      if (!currentAccount) {
        console.log("getToken: No authenticated user");
        return null;
      }

      // Use the cached user info to determine account type
      const currentUserInfo = getCurrentUser(currentAccount);
      if (!currentUserInfo) {
        console.log("getToken: Could not get user info");
        return null;
      }

      const isB2C = currentUserInfo.authSource === "b2c";

      try {
        // Use the appropriate MSAL instance
        const instance = isB2C ? b2cMsalInstance : msalInstance;
        // Use the appropriate scope based on the user type
        const requestedScopes = isB2C
          ? [apiScopes.participantScope]
          : [apiScopes.adminScope];

        console.log(
          `[TOKEN REFRESH] Getting token for ${
            isB2C ? "B2C" : "Entra ID"
          } user`,
          {
            username: currentAccount.username,
            homeAccountId: currentAccount.homeAccountId,
            scopes: requestedScopes,
            forceRefresh,
            usingInstance: isB2C ? "B2C" : "Entra",
          }
        );

        // Ensure B2C silent refresh uses the same policy the user logged in with
        const authorityForAccount = isB2C
          ? `https://${b2cConfig.tenantName}.b2clogin.com/${
              b2cConfig.tenantName
            }.onmicrosoft.com/${
              currentAccount?.idTokenClaims?.tfp ||
              currentAccount?.idTokenClaims?.acr ||
              b2cConfig.signInEmailFlowName
            }`
          : undefined;

        const response = await instance.acquireTokenSilent({
          scopes: requestedScopes,
          account: currentAccount,
          forceRefresh,
          ...(authorityForAccount ? { authority: authorityForAccount } : {}),
        });

        if (response) {
          console.log("Token acquired successfully");
          return response.accessToken;
        }
      } catch (error) {
        console.error("Error acquiring token:", error);

        // Handle B2C policy mismatch error
        if (
          error.errorCode === "invalid_grant" &&
          error.errorMessage?.includes("B2C")
        ) {
          console.log(
            "B2C policy mismatch detected, clearing tokens and prompting re-login"
          );
          // Clear all MSAL tokens
          await Promise.all([
            msalInstance.clearCache(),
            b2cMsalInstance.clearCache(),
          ]);
          setCurrentAccount(null);
          setErrorDialog({
            open: true,
            title: "Authentication Error",
            message:
              "There was an issue with your authentication. Please sign in again.",
          });
          return null;
        }

        // Handle token acquisition failure - prompt for login if necessary
        if (error.name === "InteractionRequiredAuthError") {
          setErrorDialog({
            open: true,
            title: "Session Expired",
            message: "Your session has expired. Please sign in again.",
          });
        }
      }

      return null;
    },
    [currentAccount, b2cMsalInstance, msalInstance, getCurrentUser]
  );

  // Handle pending redirect (after login redirect) - Reverted to Promise.all version
  const handlePendingRedirect = useCallback(async () => {
    console.log("Handling any pending redirects (Promise.all)");

    // Check if a registration might be in progress *before* handling redirect
    // Check for either invite_token (legacy) or qr_enrollment_data (QR flow)
    const inviteTokenFromStorage = localStorage.getItem("invite_token");
    const qrEnrollmentData = localStorage.getItem("qr_enrollment_data");
    const qrUserId = localStorage.getItem("qr_user_id");

    if (inviteTokenFromStorage) {
      console.log(
        "Potential registration detected (invite_token found in localStorage)"
      );
    }
    if (qrEnrollmentData && qrUserId) {
      console.log(
        "Potential QR registration detected (qr_enrollment_data found in localStorage)"
      );
    }

    try {
      // Try to handle redirects from both MSAL instances
      const [entraResponse, b2cResponse] = await Promise.all([
        msalInstance.handleRedirectPromise().catch((e) => {
          console.log(
            "Entra redirect handling error (expected if not an Entra redirect):",
            e.message
          );
          return null;
        }),
        b2cMsalInstance.handleRedirectPromise().catch((e) => {
          console.log(
            "B2C redirect handling error (expected if not a B2C redirect):",
            e.message
          );
          return null;
        }),
      ]);

      // Log redirect response types
      console.log("Redirect responses:", {
        entraResponse: entraResponse ? "success" : "none",
        b2cResponse: b2cResponse ? "success" : "none",
      });

      // Set the account from whichever response contains one
      if (entraResponse && entraResponse.account) {
        console.log(
          "Setting account from Entra ID response:",
          entraResponse.account.username
        );
        setCurrentAccount(entraResponse.account);
        // Clear potential registration flag if Entra login occurred
        localStorage.removeItem("registration_data"); // Use localStorage
        localStorage.removeItem("invite_token"); // Also clear invite token
        return { success: true, account: entraResponse.account };
      }

      if (b2cResponse && b2cResponse.account) {
        console.log(
          "Setting account from B2C response:",
          b2cResponse.account.username
        );
        setCurrentAccount(b2cResponse.account);

        // Check for registration completion (either invite or QR flow)
        if (inviteTokenFromStorage || (qrEnrollmentData && qrUserId)) {
          console.log(
            "B2C redirect successful and registration data was present. Storing registration data."
          );
          const registrationData = {
            isRegistrationCompletion: true,
            userId: b2cResponse.account.idTokenClaims?.oid, // B2C Object ID
            accountName: b2cResponse.account.name,
            accountEmail: b2cResponse.account.username,
            // Add policy claim (tfp or acr) to distinguish provider type later
            policy:
              b2cResponse.account.idTokenClaims?.tfp ||
              b2cResponse.account.idTokenClaims?.acr,
            // Indicate which registration flow was used
            isQRFlow: !inviteTokenFromStorage && qrEnrollmentData && qrUserId,
          };
          localStorage.setItem(
            "registration_data",
            JSON.stringify(registrationData)
          ); // Use localStorage
          // DO NOT remove tokens/data here - Participant.js needs them and will clean up
        } else {
          // Clear potential registration flag if normal B2C login occurred
          localStorage.removeItem("registration_data"); // Use localStorage
          localStorage.removeItem("invite_token"); // Also clear invite token
          localStorage.removeItem("qr_enrollment_data");
          localStorage.removeItem("qr_user_id");
        }

        return { success: true, account: b2cResponse.account };
      }

      console.log("No account found in redirect responses");
      return { success: false };
    } catch (error) {
      console.error("Error handling redirects:", error);
      return { success: false, error };
    }
  }, [msalInstance, b2cMsalInstance]); // Keep both dependencies

  // Initialize the MSAL instances and check for existing accounts
  useEffect(() => {
    const initializeMsal = async () => {
      if (isInitializing.current || initialized) {
        return;
      }

      isInitializing.current = true;
      console.log("Initializing MSAL Context..."); // Slightly changed log

      try {
        // first initialise the instances
        await Promise.all([
          msalInstance.initialize(),
          b2cMsalInstance.initialize(),
        ]);
        console.log("MSAL instances initialized.");

        // --- Schedule redirect handling and cache check for the next event loop tick ---
        console.log("Scheduling redirect/account check...");
        setTimeout(async () => {
          try {
            console.log("Executing delayed redirect/account check...");
            const redirectResult = await handlePendingRedirect();
            console.log("Delayed redirect handling result:", redirectResult);

            // If redirect successful, we're initialized and done.
            if (redirectResult.success && redirectResult.account) {
              console.log("Account set from delayed redirect check.");
              setInitialized(true); // Mark initialized
              isInitializing.current = false; // Mark complete
              return; // Exit callback
            }

            // If no redirect account, check cache
            console.log(
              "No account from redirect, checking cached accounts..."
            );
            const entraAccounts = msalInstance.getAllAccounts();
            const b2cAccounts = b2cMsalInstance.getAllAccounts();

            console.log("Existing accounts:", {
              entraCount: entraAccounts.length,
              b2cCount: b2cAccounts.length,
            });

            // Define account checking logic
            const checkAccount = async (instance, account) => {
              try {
                const isB2C = isB2CAccount(account);
                const scopes = isB2C
                  ? [apiScopes.participantScope]
                  : [apiScopes.adminScope];
                const authorityForAccount = isB2C
                  ? `https://${b2cConfig.tenantName}.b2clogin.com/${
                      b2cConfig.tenantName
                    }.onmicrosoft.com/${
                      account?.idTokenClaims?.tfp ||
                      account?.idTokenClaims?.acr ||
                      b2cConfig.signInEmailFlowName
                    }`
                  : undefined;
                await instance.acquireTokenSilent({
                  scopes,
                  account,
                  forceRefresh: false,
                  ...(authorityForAccount
                    ? { authority: authorityForAccount }
                    : {}),
                });
                return true;
              } catch (error) {
                // Handle B2C policy mismatch gracefully - just mark as invalid, don't throw
                if (
                  error.errorCode === "invalid_grant" &&
                  error.errorMessage?.includes("B2C")
                ) {
                  console.log(
                    `Account ${account.username} has policy mismatch - marking as invalid:`,
                    error.message
                  );
                  return false;
                }

                console.log(
                  `Account ${account.username} in ${
                    isB2CAccount(account) ? "B2C" : "Entra ID"
                  } not valid:`,
                  error.message
                );
                return false;
              }
            };

            // Define logic to check all accounts
            const checkAllAccounts = async () => {
              const validEntraAccountsPromises = entraAccounts.map((account) =>
                checkAccount(msalInstance, account).then((isValid) =>
                  isValid ? { account } : null
                )
              );
              const validB2CAccountsPromises = b2cAccounts.map((account) =>
                checkAccount(b2cMsalInstance, account).then((isValid) =>
                  isValid ? { account } : null
                )
              );

              const allResults = await Promise.all([
                ...validEntraAccountsPromises,
                ...validB2CAccountsPromises,
              ]);
              const validAccounts = allResults
                .filter((result) => result !== null)
                .sort(
                  (a, b) =>
                    (b.account.idTokenClaims?.auth_time || 0) -
                    (a.account.idTokenClaims?.auth_time || 0)
                );

              console.log("Valid accounts found:", validAccounts.length);
              if (validAccounts.length > 0) {
                const mostRecent = validAccounts[0];
                const isB2C = isB2CAccount(mostRecent.account);
                console.log(
                  `Setting most recent account: ${
                    mostRecent.account.username
                  } (${isB2C ? "B2C" : "Entra ID"})`
                );
                setCurrentAccount(mostRecent.account);
              } else {
                console.log("No valid accounts found");
                setCurrentAccount(null);
              }
            };

            // Execute the account check
            await checkAllAccounts();
            setInitialized(true); // Mark initialized after cache check
          } catch (error) {
            // Catch errors from within the setTimeout logic (redirect or cache check)
            console.error(
              "Error during delayed redirect/account check:",
              error
            );
            setErrorDialog({
              open: true,
              title: "Authentication Check Error",
              message:
                "There was an error checking your session after login. Please try refreshing.",
            });
            setInitialized(true); // Ensure initialized is set even on error
          } finally {
            // This finally block runs after the try/catch within the setTimeout completes
            isInitializing.current = false; // Mark initialization attempt as complete
          }
        }, 0); // Zero delay
      } catch (error) {
        // Catch errors ONLY from the initial .initialize() calls
        console.error("MSAL core initialization error:", error);
        setErrorDialog({
          open: true,
          title: "Authentication Setup Error",
          message:
            "There was an error setting up authentication. Please try refreshing the page.",
        });
        // Mark as initialized and not initializing on core init error
        setInitialized(true); // Still set initialized true on core error
        isInitializing.current = false; // Ensure flag is reset on core error
      }
      // Note: isInitializing.current = false is now primarily handled within the setTimeout's finally block
      // We don't set initialized=true here anymore, it's handled within the timeout or the catch block
    };

    initializeMsal();
    // Cleanup function for useEffect might be needed if timeouts could overlap, but likely okay here.
  }, [handlePendingRedirect, msalInstance, b2cMsalInstance, initialized]);

  // Replay cached uploads ONCE per login, after the app has settled — not on
  // every token refresh (which fired the whole queue on every API call).
  const replayScheduledRef = useRef(false);
  useEffect(() => {
    if (!(initialized && currentAccount) || replayScheduledRef.current) return;
    replayScheduledRef.current = true;
    const id = setTimeout(() => {
      getToken()
        .then((token) => processFailedUploads(token))
        .catch((e) => console.warn("Cached upload replay skipped:", e?.message || e));
    }, 2000);
    return () => clearTimeout(id);
  }, [currentAccount, initialized, getToken]);

  // Context value to provide to consumers
  const contextValue = {
    entra: msalInstance,
    b2c: b2cMsalInstance,
    currentAccount,
    currentUser: getCurrentUser(currentAccount),
    isInitialized: initialized,
    isAuthenticated: initialized && !!currentAccount,
    isB2C: (account) => isB2CAccount(account || currentAccount),
    handlePendingRedirect,
    getToken,
    logout,
    setErrorDialog,
    errorDialog,
  };

  return (
    <DualMsalContext.Provider value={contextValue}>
      {children}
      <ErrorDialog
        open={errorDialog.open}
        onClose={handleErrorDialogClose}
        title={errorDialog.title}
        message={errorDialog.message}
      />
    </DualMsalContext.Provider>
  );
}
